import type { SubjectArea, SubjectAreaRef } from '@/types';
import type { MultiSelectOption } from '@ps-refarch-ux/neon';
import { SCEDSubjectCodes, SUBJECT_AREAS_TEXT } from '@/constants/subject-area-constants';

/** Shape shared by `SubjectArea` and course `SubjectAreaRef` for select keys / labels. */
export type SubjectAreaOptionSource = Pick<SubjectArea, 'name'> &
  Partial<Pick<SubjectArea, 'CSSCCourse' | 'SCEDSubject'>>;

export type SubjectAreaSortDirection = 'asc' | 'desc';
export type SubjectAreaSortState = {
  colId: keyof Pick<SubjectArea, 'name' | 'SCEDSubject'>;
  sort: SubjectAreaSortDirection;
};

export const isDuplicateSubjectArea = (
  name: string,
  scedCode: number,
  existingAreas: SubjectArea[],
  excludeId?: string
): boolean => {
  const normalized = name.trim().toLowerCase();
  if (normalized === '' || Number.isNaN(scedCode)) {
    return false;
  }

  return existingAreas.some(
    (sa) =>
      sa.name.trim().toLowerCase() === normalized &&
      sa.SCEDSubject === scedCode &&
      (excludeId === undefined || sa.id !== excludeId)
  );
};

export const toolTipAssociatedCourses = (count: number) => {
  return `${SUBJECT_AREAS_TEXT.EditAndDeleteToolTip}\nAssociated courses: ${count}`;
};

export const formatScedDisplayText = (description: string): string => {
  const match = description.match(/^(\d{2})\s+(.+)$/);
  if (!match) {
    return description;
  }

  const [, code, text] = match;
  return `${code} - ${text}`;
};

/**
 * Stable select value for a subject area (matches legacy c-p-ui `subjectAreaToString`).
 * Disambiguates rows that share a name but differ by SCED / CSSC.
 */
export function subjectAreaToOptionValue(sa: SubjectAreaOptionSource): string {
  const cssc = sa.CSSCCourse ?? 0;
  const sced = sa.SCEDSubject ?? 0;
  return `${sa.name}_${cssc}_${sced}`;
}

/**
 * Dropdown label: `Name (SCED …)` using SCED list description when available, else numeric code.
 * Aligns with legacy CourseForm `getSubjectAreaList` display.
 */
export function formatSubjectAreaOptionLabel(sa: SubjectAreaOptionSource): string {
  const { name } = sa;
  const sced = sa.SCEDSubject;
  if (typeof sced === 'number' && sced > 0) {
    const row = SCEDSubjectCodes.find((c) => c.code === sced);
    if (row) {
      return `${name} (${formatScedDisplayText(row.description)})`;
    }
    return `${name} (${sced})`;
  }
  const cssc = sa.CSSCCourse;
  if (typeof cssc === 'number' && cssc > 0) {
    return `${name} (${cssc})`;
  }
  return name;
}

/** Builds uniquely keyed select options so subject areas with the same name remain independent. */
export function buildSubjectAreaSelectOptions(subjectAreas: SubjectArea[]): MultiSelectOption[] {
  return subjectAreas.map((subjectArea) => {
    const optionValue = subjectAreaToOptionValue(subjectArea);
    return {
      id: optionValue,
      name: optionValue,
      labelText: subjectArea.name,
    };
  });
}

/** Converts composite select values to the plain names accepted by the course catalog API. */
export function subjectAreaNamesFromOptionValues(optionValues: string[]): string[] {
  return Array.from(
    new Set(
      optionValues
        .map((optionValue) => parseSubjectAreaOptionValue(optionValue)?.name)
        .filter((name): name is string => Boolean(name))
    )
  );
}

/** Checks a course subject-area reference against the selected composite option values. */
export function subjectAreaRefMatchesOptionValues(
  subjectArea: SubjectAreaRef | undefined,
  optionValues: string[]
): boolean {
  if (optionValues.length === 0) {
    return true;
  }
  if (!subjectArea) {
    return false;
  }

  return optionValues.includes(subjectAreaToOptionValue(subjectArea));
}

export function findSubjectAreaByOptionValue(
  list: SubjectArea[],
  optionValue: string
): SubjectArea | undefined {
  return list.find((sa) => subjectAreaToOptionValue(sa) === optionValue);
}

/**
 * Inverse of `subjectAreaToOptionValue` (handles names that contain `_` by taking the last two segments as CSSC and SCED).
 */
export function parseSubjectAreaOptionValue(
  value: string
):
  | (Pick<SubjectAreaOptionSource, 'name'> & { CSSCCourse: number; SCEDSubject: number })
  | undefined {
  const last = value.lastIndexOf('_');
  if (last <= 0) {
    return undefined;
  }
  const secondLast = value.lastIndexOf('_', last - 1);
  if (secondLast < 0) {
    return undefined;
  }
  const name = value.slice(0, secondLast);
  if (!name) {
    return undefined;
  }
  const cssc = Number(value.slice(secondLast + 1, last));
  const sced = Number(value.slice(last + 1));
  if (Number.isNaN(cssc) || Number.isNaN(sced)) {
    return undefined;
  }
  return { name, CSSCCourse: cssc, SCEDSubject: sced };
}

/** Resolve dropdown value to API `SubjectAreaRef`, including parsed fallback when the row is not in `list`. */
export function subjectAreaRefFromSelectValue(
  optionValue: string,
  list: SubjectArea[]
): SubjectAreaRef | undefined {
  const trimmed = optionValue.trim();
  if (!trimmed) {
    return undefined;
  }
  const fromList = findSubjectAreaByOptionValue(list, trimmed);
  if (fromList) {
    return {
      name: fromList.name,
      CSSCCourse: fromList.CSSCCourse ?? 0,
      SCEDSubject: fromList.SCEDSubject ?? 0,
    };
  }
  const parsed = parseSubjectAreaOptionValue(trimmed);
  if (parsed) {
    return {
      name: parsed.name,
      CSSCCourse: parsed.CSSCCourse,
      SCEDSubject: parsed.SCEDSubject,
    };
  }
  return undefined;
}

export const sortSubjectAreas = (
  rows: SubjectArea[],
  sortState: SubjectAreaSortState | null
): SubjectArea[] => {
  const sorted = [...rows];
  if (!sortState) {
    sorted.sort((a, b) => {
      const byName = (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'case' });
      if (byName !== 0) {
        return byName;
      }
      return (a.SCEDSubject || 0) - (b.SCEDSubject || 0);
    });
    return sorted;
  }

  const { colId, sort } = sortState;
  const direction = sort === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    if (colId === 'SCEDSubject') {
      return ((a.SCEDSubject || 0) - (b.SCEDSubject || 0)) * direction;
    }

    const byName =
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'case' }) * direction;
    if (byName !== 0) {
      return byName;
    }
    return ((a.SCEDSubject || 0) - (b.SCEDSubject || 0)) * direction;
  });

  return sorted;
};
